import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import { keyframes } from "@mui/system";
import DtvText, { IDtvTextProps } from "@directv-digital/dds.ui.dtv-text";

// const frames = keyframes`
//   20%{background-position:0%   0%, 50%  50%,100%  50%}
//   40%{background-position:0% 100%, 50%   0%,100%  50%}
//   60%{background-position:0%  50%, 50% 100%,100%   0%}
//   80%{background-position:0%  50%, 50%  50%,100% 100%}
// `;

const frames = keyframes`
  20%{background-position:0%   0%, 33%  50%,67%  50%,100%  50%}
  35%{background-position:0% 100%, 33%   0%,67%  50%,100%  50%}
  50%{background-position:0%  50%, 33% 100%,67%   0%,100%  50%}
  65%{background-position:0%  50%, 33%  50%,67% 100%,100%  0%}
  80%{background-position:0%  50%, 33%  50%,67%  50%,100%  100%}
`;

export const LoadingDots = ({ styles }: { styles?: any }) => (
    <DtvBox sx={{
      display: "inline-block",
      width: "32px",
      aspectRatio: "3",
      "--_g": "no-repeat radial-gradient(circle closest-side,#000 90%,#0000)",
      background: "var(--_g) 0% 50%,\n    var(--_g) 33% 50%,\n    var(--_g) 67% 50%,\n    var(--_g) 100% 50%",
      backgroundSize: "calc(100%/4) 50%",
      animation: `${frames} 1s infinite linear`,
      mx: '4px',
      ...styles,
      my: "0 !important" // safeguard
      }}
    ></DtvBox>
)

/*
* Regex Explanation:
* (1) `<br\s*\/?>` - Matches line breaks, allowing for optional self-closing tags.
* (2) `<\/?[^>]+>` - Matches HTML tags, both opening and closing.
* (3) // `[[^[\]]+\]` // \[[^\]]*\] - Matches indicator tags like [rsn_fee], [total_price], etc.
* (4) `([^<[]+)` - Matches plain text content, including punctuation and spaces.
*/
const MATCH_BR = /<br\s*\/?>/i;
const MATCH_HTML_TAG = /<\/?[^>]+>/;
const MATCH_INDICATOR = /\[[^\]]*\]/;
const MATCH_TEXT = /[^<[]+/;
const MATCH_ALL = new RegExp(
  `${MATCH_BR.source}|${MATCH_HTML_TAG.source}|${MATCH_INDICATOR.source}|${MATCH_TEXT.source}`,
  'gi'
);

export const LoadingTextBlock = ({
  content,
  contentStyles = {},
  dotsStyles = {},
  cta = {},
  tooltip = {}
}: {
  content: string;
  contentStyles: IDtvTextProps;
  dotsStyles?: any;
  cta?: IDtvTextProps['cta'];
  tooltip?: IDtvTextProps['tooltip'];
}) => {
  const tagStack: string[] = [];
  const elements: React.ReactNode[] = [];
  let keyCounter = 0;

  const parts = content?.match(MATCH_ALL) ?? [];
  // console.debug("[LoaderText] Rendering chunks :: ", parts);

  for (const [index, chunk] of parts.entries()) {
    if (!chunk) {
      continue;
    }

    let content: React.ReactNode;

    // Handle <br>, <br/>, and <br />
    if (MATCH_BR.test(chunk)) {
      // eslint-disable-next-line no-plusplus
      content = <br key={`br-${keyCounter++}`} />;
    } else if (MATCH_HTML_TAG.test(chunk)) {
      // Handle HTML open/close tags
      const isClosing = chunk.startsWith('</');
      const tagName = chunk.replace(/[</>\s]/g, '').toLowerCase();

      if (isClosing) {
        // Pop the tag from the stack to stop wrapping
        tagStack.pop();
      } else {
        // Push the tag onto the stack to begin wrapping
        tagStack.push(tagName);
      }
      continue; // No output for opening/closing tags, just manage the stack
    } else if (MATCH_INDICATOR.test(chunk)) {
      // Handle match indicators like [rsn_fee], [total_price], etc.
      // eslint-disable-next-line no-plusplus
      elements.push(<LoadingDots key={`dots-${keyCounter++}`} styles={dotsStyles} />);
      continue; // Skip to the next chunk
    } else if (MATCH_TEXT.test(chunk)) {
      // Fallback to plain text content
      // eslint-disable-next-line no-plusplus
      content = <DtvText content={chunk} key={`fallback-text-${keyCounter++}`} { ...contentStyles }
                  { ...((index === parts.length - 1) && {
 cta,
tooltip
}) }
                />;
    }

    // Wrap the content in all active tags to preserve the structure
    if (content !== undefined) {
      let wrapped = content;
      // eslint-disable-next-line no-plusplus
      for (let i = tagStack.length - 1; i >= 0; i--) {
        const tag = tagStack[i];
        wrapped = React.createElement(
          tag,
          {
            // eslint-disable-next-line no-plusplus
            key: `wrapper-${keyCounter++}`,
            style: { display: 'inline' }
          },
          wrapped
        );
      }
      elements.push(wrapped);
    }
  }

  return <>{elements}</>;
}
